import { useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import { type Recapito, type TipoRecapito, useSalvaRecapito } from '../api/scheda.js';
import { CampoBc } from '../componenti/CampoBc.js';
import { Modale } from '../componenti/Modale.js';
import { tipiRecapito } from '../etichette.js';
import { t } from '../i18n/lingua.js';

/**
 * Un recapito: e-mail, telefono o sito.
 *
 * Sulle e-mail ci sono anche le sottoscrizioni agli avvisi, e sono la parte
 * che conta di più: è qui che si decide chi riceve la mail quando l'ETA di un
 * ordine si sposta. Nel v2 sono tre caselle senza spiegazione; qui ognuna
 * dice a che cosa serve.
 */

const CONFIG: Record<
  TipoRecapito,
  { titolo: string; etichetta: string; campo: keyof Recapito; tipi: string[] }
> = {
  emails: {
    titolo: t('Indirizzo e-mail'),
    etichetta: t('Indirizzo e-mail'),
    campo: 'email',
    tipi: ['main', 'office', 'home', 'other'],
  },
  phones: {
    titolo: t('Numero di telefono'),
    etichetta: t('Numero'),
    campo: 'phoneNumber',
    tipi: ['main', 'mobile', 'office', 'fax', 'home', 'other'],
  },
  websites: {
    titolo: t('Sito web'),
    etichetta: t('Indirizzo del sito'),
    campo: 'url',
    tipi: ['main', 'other'],
  },
};

const AVVISI = [
  {
    chiave: 'notifyEtaDeviation' as const,
    testo: t('Ritardi sull’arrivo — quando il vettore sposta l’ETA di oltre sette giorni'),
  },
  {
    chiave: 'notifyStateDowngrade' as const,
    testo: t('Ordine tornato indietro di stato'),
  },
  {
    chiave: 'notifyPricelistApproval' as const,
    testo: t('Listini da approvare'),
  },
];

export function ModaleRecapito({
  identityId,
  tipo,
  recapito,
  contactId,
  limiti,
  onChiudi,
}: {
  identityId: number;
  tipo: TipoRecapito;
  recapito: Recapito | null;
  /** Se il recapito è di una persona e non dell'azienda. */
  contactId?: number | null;
  limiti: Record<string, number>;
  onChiudi: () => void;
}) {
  const c = CONFIG[tipo];
  const [campi, setCampi] = useState({
    valore: (recapito?.[c.campo] as string | undefined) ?? '',
    kind: recapito?.kind ?? '',
    isMain: recapito?.isMain ?? false,
    notifyEtaDeviation: recapito?.notifyEtaDeviation ?? false,
    notifyStateDowngrade: recapito?.notifyStateDowngrade ?? false,
    notifyPricelistApproval: recapito?.notifyPricelistApproval ?? false,
  });

  const salva = useSalvaRecapito(identityId, tipo);
  const errore = salva.error instanceof ErroreApi ? salva.error : null;

  const invia = () =>
    salva.mutate(
      {
        ...(recapito ? { id: recapito.id } : {}),
        valore: campi.valore.trim(),
        kind: campi.kind || null,
        contactId: recapito ? (recapito.contactId ?? null) : (contactId ?? null),
        isMain: campi.isMain,
        ...(tipo === 'emails'
          ? {
              notifyEtaDeviation: campi.notifyEtaDeviation,
              notifyStateDowngrade: campi.notifyStateDowngrade,
              notifyPricelistApproval: campi.notifyPricelistApproval,
            }
          : {}),
      },
      { onSuccess: onChiudi },
    );

  return (
    <Modale
      occhiello={recapito ? t('Modifica {cosa}', { cosa: c.titolo.toLowerCase() }) : t('Nuovo {cosa}', { cosa: c.titolo.toLowerCase() })}
      titolo={c.titolo}
      inCorso={salva.isPending}
      onChiudi={onChiudi}
      azioni={
        <>
          <button className="btn btn-ghost" onClick={onChiudi} disabled={salva.isPending}>
            {t('Annulla')}
          </button>
          <span className="spacer" />
          <button className="btn btn-primary" onClick={invia} disabled={salva.isPending}>
            {salva.isPending ? 'Salvo…' : 'Salva'}
          </button>
        </>
      }
    >
      {errore && (
        <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
          <span className="avviso-segno">▲</span>
          <span>{errore.message}</span>
        </div>
      )}

      <div className="fields">
        <CampoBc
          id="r-valore"
          etichetta={c.etichetta}
          obbligatorio
          larghezzaPiena
          monospazio={tipo !== 'websites'}
          valore={campi.valore}
          limite={tipo === 'emails' ? limiti.email : tipo === 'phones' ? limiti.phone : undefined}
          onCambia={(v) => setCampi((s) => ({ ...s, valore: v }))}
        />

        <div className="field">
          <label htmlFor="r-tipo">{t('Tipo')}</label>
          <select
            className="select"
            id="r-tipo"
            value={campi.kind}
            onChange={(e) => setCampi((s) => ({ ...s, kind: e.target.value }))}
          >
            <option value="">{t('Non specificato')}</option>
            {tipiRecapito(c.tipi).map((t) => (
              <option key={t.valore} value={t.valore}>
                {t.etichetta}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="switch-row" style={{ marginTop: 10 }}>
        <button
          className="switch"
          role="switch"
          aria-checked={campi.isMain}
          onClick={() => setCampi((s) => ({ ...s, isMain: !s.isMain }))}
        />
        <span>{t('Recapito predefinito — è quello che si usa per primo')}</span>
      </div>

      {tipo === 'emails' && (
        <>
          <div className="card-title" style={{ marginTop: 16, marginBottom: 4 }}>
            {t('Avvisi automatici')}
          </div>
          {AVVISI.map((a) => (
            <div className="switch-row" key={a.chiave}>
              <button
                className="switch"
                role="switch"
                aria-checked={campi[a.chiave]}
                onClick={() => setCampi((s) => ({ ...s, [a.chiave]: !s[a.chiave] }))}
              />
              <span>{a.testo}</span>
            </div>
          ))}
          <p className="hint" style={{ marginTop: 8 }}>
            {t('Le mail partono da sole quando succede: chi non è spuntato qui non riceve niente.')}
          </p>
        </>
      )}
    </Modale>
  );
}
